/**
 * Recibo de un abono o anticipo (alcance SAL-01): lo que el cliente entregó, por qué medios y lo que quedó en su saldo
 * a favor. Media carta vertical, con firma de quien recibe. No es un documento fiscal.
 * El abono no lleva IGTF (pedido de la dueña 2026-10-19): entra completo al saldo y el recibo no habla de IGTF; el 3 %
 * se cobra en la factura que se pague con ese saldo. Solo la reimpresión de un recibo anterior, que sí lo descontó,
 * trae la línea del IGTF (dice lo que pasó: lo recibido menos ese IGTF es lo abonado).
 * A la dueña y al contador, fuera del papel, se les dice cuánto del abono entró a la parte del saldo en divisas sin IGTF
 * (hallazgo T20): es un dato interno, no del cliente, y no se imprime.
 */
import { formatearExacto, formatearTasa, METODO_PAGO_ETIQUETA, Money, type MetodoPago, type MovimientoSaldoDTO, type ReciboAbonoDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { EnviarCorreo } from '@/componentes/EnviarCorreo.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuthOpcional } from '@/lib/auth.tsx';
import { useRegistroImpresion } from '@/lib/impresion.ts';
import { veParteDivisa } from '@/lib/tasa2.ts';
import { formatearFechaHora } from '@/lib/utils.ts';

const usd = (v: string) => `$ ${Money.parse(v).toDisplay()}`;
/** Cada impresión queda en la auditoría; desde la segunda es reimpresión (hallazgo D4). */
const registrarImpresionAbono = (id: string) => post<{ reimpresion: boolean }>(`/ventas/abonos/${id}/impresion`).then((r) => r.reimpresion);

export function ImprimirAbono() {
  const { id } = useParams<{ id: string }>();
  const r = useQuery({ queryKey: ['ventas', 'abono', id], queryFn: () => get<ReciboAbonoDTO>(`/ventas/abonos/${id}`), enabled: !!id });
  const re = useRegistroImpresion(id, registrarImpresionAbono);
  const empresa = useQuery({ queryKey: ['empresa'], queryFn: () => get<{ razon_social: string; rif: string; direccion_fiscal: string }>('/sucursales/empresa') });
  // El movimiento del saldo de este abono, con su parte en divisas sin IGTF: solo para la dueña y el contador
  const veParte = veParteDivisa(useAuthOpcional()?.usuario);
  const clienteId = r.data?.cliente_id;
  const movimiento = useQuery({
    queryKey: ['clientes', clienteId, 'movimientos-saldo', id],
    queryFn: () => get<MovimientoSaldoDTO[]>(`/clientes/${clienteId}/movimientos-saldo`, { referencia_id: id! }).then((ms) => ms.find((m) => m.tipo === 'abono') ?? null),
    enabled: veParte && !!clienteId && !!id,
  });
  useEffect(() => {
    if (r.data && empresa.data && re !== null) {
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [r.data, empresa.data, re]);
  if (r.error) return <p style={{ padding: 24 }}>{mensajeDeError(r.error)}</p>;
  if (!r.data || !empresa.data || re === null) return <p style={{ padding: 24 }}>Preparando recibo…</p>;
  const d = r.data;
  // Solo los recibos anteriores al 2026-10-19: desde entonces el abono no lleva IGTF
  const conIgtf = Money.parse(d.igtf_divisa).esPositivo();
  return (
    <>
    {/* Fuera del papel: enviar el recibo al correo registrado del cliente (pedido de la dueña 2026-10-19) */}
    <div className="no-print" style={{ width: '5.1in', margin: '12px auto 0', display: 'flex', justifyContent: 'flex-end' }}>
      <EnviarCorreo tipo="abono" id={d.id} />
    </div>
    {veParte && movimiento.data?.divisa_sin_igtf && movimiento.data.divisa_sin_igtf_despues ? (
      <div className="no-print mx-auto mt-2 rounded-lg border bg-muted/60 px-3 py-2 text-xs" style={{ width: '5.1in' }} data-testid="abono-parte-divisa">
        <p>De este abono, <b>{usd(movimiento.data.divisa_sin_igtf)}</b> entraron al saldo en divisas sin IGTF: pagan el 3 % de IGTF cuando se usen en una factura o una nota de débito. Después del abono, esa parte del saldo quedó en <b>{usd(movimiento.data.divisa_sin_igtf_despues)}</b>.</p>
        <p className="mt-0.5 text-muted-foreground">Solo lo ven la dueña y el contador. No sale en el papel.</p>
      </div>
    ) : null}
    <div className="formato" data-testid="formato-abono" data-formato="media_carta_vertical" style={{ fontFamily: 'Georgia, serif', fontSize: 10.5, width: '5.1in', margin: '0 auto', padding: 6 }}>
      <style>{`@page { size: 5.5in 8.5in; margin: 5mm } html, body { background: #fff; margin: 0 } .formato td, .formato th { padding: 3px 4px; vertical-align: top; text-align: left } .formato .num { text-align: right; font-variant-numeric: tabular-nums } @media screen { body { background: #e5e7eb } .formato { background: #fff } }`}</style>
      <div style={{ borderBottom: '2px solid #000', paddingBottom: 4, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 'bold' }}>{empresa.data.razon_social}</div>
        <div>RIF {empresa.data.rif} · Sucursal {d.sucursal_nombre}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 4 }}>
          <div style={{ fontWeight: 'bold', fontSize: 11.5 }}>RECIBO DE ABONO{re ? <span data-testid="formato-abono-reimpresion"> · REIMPRESIÓN</span> : null}</div>
          <div style={{ textAlign: 'right' }}>
            <div>N° <b data-testid="formato-abono-numero">{d.numero_formateado ?? '—'}</b></div>
            <div>{formatearFechaHora(d.recibido_en)}</div>
          </div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr><td style={{ width: '32%' }}><b>Recibimos de</b></td><td data-testid="formato-abono-cliente">{d.cliente_razon_social} · {d.cliente_rif}</td></tr>
          <tr><td><b>Recibido por</b></td><td>{d.usuario_nombre}{d.caja_nombre ? ` · ${d.caja_nombre}` : ''}</td></tr>
        </tbody>
      </table>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
        <thead><tr style={{ borderBottom: '1px solid #000' }}><th>Forma de pago</th><th>Referencia</th><th className="num">Monto</th></tr></thead>
        <tbody>
          {d.lineas.map((l) => (
            <tr key={l.id} data-testid="formato-abono-linea">
              <td>{METODO_PAGO_ETIQUETA[l.metodo as MetodoPago] ?? l.metodo}</td>
              <td>{l.referencia ?? '—'}{l.pagador ? ` · ${l.pagador}` : ''}</td>
              <td className="num">{usd(l.monto_usd)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '1px solid #000' }}><td colSpan={2}><b>Total recibido</b></td><td className="num" data-testid="formato-abono-recibido"><b>{usd(d.total_usd)}</b></td></tr>
          {Money.parse(d.vuelto_usd).esPositivo() ? <tr><td colSpan={2}>Vuelto entregado</td><td className="num">{usd(d.vuelto_usd)}</td></tr> : null}
          {conIgtf ? <tr><td colSpan={2}>IGTF {Money.parse(d.igtf_pct).toString()} % percibido (Bs {Money.parse(d.igtf_bs).toDisplay()}{d.tasa_bcv_cobro ? ` a tasa BCV ${formatearTasa(d.tasa_bcv_cobro)}` : ''})</td><td className="num" data-testid="formato-abono-igtf">$ {formatearExacto(d.igtf_divisa)}</td></tr> : null}
          <tr><td colSpan={2} style={{ fontSize: 12 }}><b>Abonado a su saldo a favor</b></td><td className="num" style={{ fontSize: 12 }} data-testid="formato-abono-abonado"><b>{usd(d.abonado_usd)}</b></td></tr>
          {d.saldo_despues_usd ? <tr><td colSpan={2}>Saldo a favor después de este abono</td><td className="num" data-testid="formato-abono-saldo">{usd(d.saldo_despues_usd)}</td></tr> : null}
        </tfoot>
      </table>
      <p style={{ marginTop: 10, fontSize: 9.5 }}>Este recibo no es una factura. El saldo a favor se usa como pago en sus próximas compras y no vence.</p>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 46, fontSize: 9.5 }}>
        <span style={{ borderTop: '1px solid #000', paddingTop: 2, width: '45%', textAlign: 'center' }}>Recibe (caja)</span>
        <span style={{ borderTop: '1px solid #000', paddingTop: 2, width: '45%', textAlign: 'center' }}>Cliente</span>
      </div>
    </div>
    </>
  );
}
